import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const TOOLBAR_ACTIONS = [
  { label: 'B', command: 'bold', title: 'Bold' },
  { label: 'I', command: 'italic', title: 'Italic' },
  { label: 'U', command: 'underline', title: 'Underline' },
  { label: '• List', command: 'insertUnorderedList', title: 'Bulleted List' },
  { label: '1. List', command: 'insertOrderedList', title: 'Numbered List' }
];

const normalizeHtml = (html = '') => html.replace(/&nbsp;/g, ' ').trim();
const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'EM', 'U', 'UL', 'OL', 'LI', 'A', 'B', 'I']);
const SAFE_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

export const isRichTextEmpty = (html = '') => {
  if (!html) return true;
  const withoutTags = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<\/?(p|div|span|strong|em|u|ul|ol|li|b|i)\b[^>]*>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  return withoutTags.length === 0;
};

export const sanitizeRichTextHtml = (html = '') => {
  if (!html || typeof window === 'undefined') return '';

  const template = window.document.createElement('template');
  template.innerHTML = html;

  const sanitizeNode = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === window.Node.ELEMENT_NODE) {
        const tag = child.tagName.toUpperCase();

        if (!ALLOWED_TAGS.has(tag)) {
          const fragment = window.document.createDocumentFragment();
          while (child.firstChild) {
            fragment.appendChild(child.firstChild);
          }
          child.replaceWith(fragment);
          sanitizeNode(node);
          return;
        }

        Array.from(child.attributes).forEach((attr) => {
          const attrName = attr.name.toLowerCase();
          if (tag === 'A' && attrName === 'href') {
            try {
              const parsedUrl = new URL(attr.value, window.location.origin);
              if (!SAFE_URL_PROTOCOLS.has(parsedUrl.protocol)) {
                child.removeAttribute(attr.name);
              }
            } catch {
              child.removeAttribute(attr.name);
            }
            return;
          }

          child.removeAttribute(attr.name);
        });

        if (tag === 'A') {
          child.setAttribute('target', '_blank');
          child.setAttribute('rel', 'noopener noreferrer');
        }
      }

      if (child.nodeType !== window.Node.TEXT_NODE) {
        sanitizeNode(child);
      }
    });
  };

  sanitizeNode(template.content);
  return normalizeHtml(template.innerHTML);
};

const COMMAND_SELECTORS = {
  bold: 'b, strong',
  italic: 'i, em',
  underline: 'u',
  insertUnorderedList: 'ul',
  insertOrderedList: 'ol',
  createLink: 'a',
};

const selectionFingerprint = () => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return '';
  const range = selection.getRangeAt(0);
  return [
    selection.isCollapsed ? '1' : '0',
    range.startOffset,
    range.endOffset,
    range.startContainer === range.endContainer ? '1' : '0',
    range.startContainer.nodeName,
  ].join(':');
};

const isCommandActive = (command, editorEl) => {
  if (typeof document === 'undefined') return false;

  const selection = window.getSelection();
  const collapsed = Boolean(selection && selection.rangeCount > 0 && selection.isCollapsed);

  let queryState = null;
  try {
    queryState = document.queryCommandState(command);
  } catch {
    queryState = null;
  }

  // At a collapsed caret, queryCommandState is the pending format for the next keystroke.
  // The caret can still sit inside <strong>/<u> after toggling off, so DOM walking would
  // keep the button on until the user types.
  if (command !== 'createLink' && collapsed && queryState !== null) {
    return queryState;
  }

  if (queryState) return true;

  if (!selection || selection.rangeCount === 0) return false;

  let node = selection.anchorNode;
  if (!node) return false;
  if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
  if (!node || !editorEl?.contains(node)) return false;

  const selector = COMMAND_SELECTORS[command];
  if (selector && node.closest?.(selector)) return true;

  if (command === 'bold') {
    const weight = window.getComputedStyle(node).fontWeight;
    return weight === 'bold' || Number(weight) >= 600;
  }
  if (command === 'italic') {
    return window.getComputedStyle(node).fontStyle === 'italic';
  }
  if (command === 'underline') {
    let el = node;
    while (el && editorEl.contains(el)) {
      const deco = window.getComputedStyle(el).textDecorationLine || window.getComputedStyle(el).textDecoration || '';
      if (deco.includes('underline')) return true;
      el = el.parentElement;
    }
  }

  return false;
};

const buttonClass = (active) => (
  `px-2 py-1 text-xs rounded transition-colors ${
    active
      ? 'bg-pink-100 text-[#ea3663] font-semibold ring-1 ring-pink-300'
      : 'text-slate-700 hover:bg-slate-200'
  }`
);

const RichTextEditor = ({ value, onChange, placeholder = '', minHeight = '8rem' }) => {
  const editorRef = useRef(null);
  const selectionRef = useRef(null);
  const lastEmittedValueRef = useRef(null);
  const pendingToolbarRef = useRef(null);
  const [activeCommands, setActiveCommands] = useState({});
  const normalizedValue = useMemo(() => (value && value.trim().length > 0 ? value : ''), [value]);

  useEffect(() => {
    if (!editorRef.current) return;
    // The emitted value is normalized, so it rarely matches innerHTML exactly (e.g. after Enter or a trailing
    // space). Rewriting innerHTML for our own edits resets the caret to the start, so only sync external changes.
    if (normalizedValue === lastEmittedValueRef.current) return;
    if (editorRef.current.innerHTML !== normalizedValue) {
      editorRef.current.innerHTML = normalizedValue;
    }
  }, [normalizedValue]);

  const syncActiveCommands = useCallback(() => {
    const editorEl = editorRef.current;
    const next = {};
    TOOLBAR_ACTIONS.forEach(({ command }) => {
      next[command] = isCommandActive(command, editorEl);
    });
    next.createLink = isCommandActive('createLink', editorEl);
    setActiveCommands((prev) => {
      const keys = Object.keys(next);
      if (keys.length === Object.keys(prev).length && keys.every((key) => prev[key] === next[key])) {
        return prev;
      }
      return next;
    });
  }, []);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      selectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  };

  const emitHtml = () => {
    if (!editorRef.current) return;
    saveSelection();
    const html = normalizeHtml(editorRef.current.innerHTML);
    lastEmittedValueRef.current = html;
    onChange(html);
  };

  const handleInput = () => {
    pendingToolbarRef.current = null;
    emitHtml();
    syncActiveCommands();
  };

  const handleCaretMove = () => {
    saveSelection();
    const pending = pendingToolbarRef.current;
    if (pending && selectionFingerprint() === pending.selection) {
      return;
    }
    pendingToolbarRef.current = null;
    syncActiveCommands();
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (!selection || !selectionRef.current || !editorRef.current) return;
    if (!editorRef.current.contains(selectionRef.current.commonAncestorContainer)) return;
    selection.removeAllRanges();
    selection.addRange(selectionRef.current);
  };

  const handleAction = (command) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    restoreSelection();
    const nextActive = !Boolean(activeCommands[command]);
    document.execCommand(command, false, null);
    pendingToolbarRef.current = { command, nextActive, selection: selectionFingerprint() };
    emitHtml();
    setActiveCommands((prev) => (
      prev[command] === nextActive ? prev : { ...prev, [command]: nextActive }
    ));
  };

  const handleAddLink = () => {
    if (!editorRef.current) return;
    const url = window.prompt('Enter URL');
    if (!url) return;
    editorRef.current.focus();
    restoreSelection();
    const nextActive = true;
    document.execCommand('createLink', false, url);
    pendingToolbarRef.current = { command: 'createLink', nextActive, selection: selectionFingerprint() };
    emitHtml();
    setActiveCommands((prev) => (
      prev.createLink === nextActive ? prev : { ...prev, createLink: nextActive }
    ));
  };

  useEffect(() => {
    const onSelectionChange = () => {
      const selection = window.getSelection();
      const editorEl = editorRef.current;
      if (!selection || !editorEl) return;
      if (!editorEl.contains(selection.anchorNode)) return;
      handleCaretMove();
    };

    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [syncActiveCommands]);

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        {TOOLBAR_ACTIONS.map((action) => {
          const active = Boolean(activeCommands[action.command]);
          return (
            <button
              key={action.command}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAction(action.command)}
              className={buttonClass(active)}
              title={action.title}
              aria-pressed={active}
            >
              {action.command === 'bold' ? <span className="font-bold">B</span>
                : action.command === 'italic' ? <span className="italic">I</span>
                : action.command === 'underline' ? <span className="underline">U</span>
                : action.label}
            </button>
          );
        })}
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleAddLink}
          className={buttonClass(Boolean(activeCommands.createLink))}
          title="Insert Link"
          aria-pressed={Boolean(activeCommands.createLink)}
        >
          Link
        </button>
      </div>
      <div className="relative">
        {isRichTextEmpty(value) && (
          <span className="pointer-events-none absolute left-3 top-2 text-sm text-slate-400">
            {placeholder}
          </span>
        )}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyUp={handleCaretMove}
          onMouseUp={handleCaretMove}
          className="w-full px-3 py-2 text-slate-800 focus:outline-none relative [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-2 [&_li]:my-1"
          style={{ minHeight }}
          suppressContentEditableWarning
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
